import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type { ReviewOutcome } from './types'
import { REVIEW_OUTCOME_BADGES, REVIEW_OUTCOME_LABELS } from './helpers'

export function LatestMetadataBadge() {
  return (
    <Badge variant="secondary" size="xs" className="shrink-0 border border-blue-200 bg-blue-50 text-blue-700">
      Latest
    </Badge>
  )
}

export function ReviewOutcomeSelect({ outcome, onChange, align = 'left' }: {
  outcome: ReviewOutcome | null
  onChange: (reviewOutcome: ReviewOutcome) => void
  align?: 'left' | 'right'
}) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  if (!outcome) {
    return <span className="text-[10px] text-muted-foreground">-</span>
  }

  const options: ReviewOutcome[] = ['unreviewed', 'accepted', 'needs_revision']

  return (
    <div className="relative inline-flex" ref={menuRef}>
      <button
        type="button"
        aria-label={`Document review status: ${REVIEW_OUTCOME_LABELS[outcome]}`}
        onClick={() => setShowMenu(!showMenu)}
        className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] cursor-pointer hover:border-gray-400 ${REVIEW_OUTCOME_BADGES[outcome]}`}
      >
        {REVIEW_OUTCOME_LABELS[outcome]}
        <ChevronDown className="h-2.5 w-2.5" />
      </button>
      {showMenu && (
        <div className={`absolute top-full z-30 mt-1 w-36 rounded-md border bg-white py-1 shadow-lg ${align === 'right' ? 'right-0' : 'left-0'}`}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setShowMenu(false)
                if (option !== outcome) onChange(option)
              }}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[11px] text-gray-700 hover:bg-gray-100 cursor-pointer"
            >
              <span>{REVIEW_OUTCOME_LABELS[option]}</span>
              {option === outcome && <Check className="h-3 w-3 text-blue-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
